import type { RouteLocationMatched } from 'vue-router'
import {
  AdminRole,
  APPLICANT_LANDING_PATH,
  MODULE_PERMISSIONS,
  SIDEBAR_NAV,
  type SidebarNavItem,
} from '@/modules/Core/constants/adminAccess'
import type {
  HrPermissionCode,
  PermissionCheckMode,
} from '@/modules/Core/constants/hrPermissions'
import { canAccessPermissions } from '@/modules/Core/utils/permissions'

function isApplicantRole(roles: readonly AdminRole[]): boolean {
  return roles.includes(AdminRole.Applicant)
}

export function getRouteRequiredPermissions(
  matched: RouteLocationMatched[]
): HrPermissionCode[] | undefined {
  for (let index = matched.length - 1; index >= 0; index -= 1) {
    const requiredPermissions = matched[index].meta.requiredPermissions

    if (requiredPermissions?.length) {
      return requiredPermissions
    }
  }

  return undefined
}

export function getRoutePermissionMode(
  matched: RouteLocationMatched[]
): PermissionCheckMode {
  for (let index = matched.length - 1; index >= 0; index -= 1) {
    const mode = matched[index].meta.permissionMode

    if (mode) {
      return mode
    }
  }

  return 'any'
}

function filterSidebarChildren(
  item: SidebarNavItem,
  userPermissions: readonly HrPermissionCode[]
): SidebarNavItem {
  if (!item.children?.length) {
    return item
  }

  const children = item.children.filter((child) => {
    if (!child.requiredPermissions?.length) {
      return true
    }

    return canAccessPermissions(
      userPermissions,
      child.requiredPermissions,
      'any'
    )
  })

  return {
    ...item,
    children,
  }
}

export function getAccessibleSidebarItems(
  userPermissions: readonly HrPermissionCode[],
  roles: readonly AdminRole[] = []
): SidebarNavItem[] {
  return SIDEBAR_NAV.filter((item) => {
    if (isApplicantRole(roles) && item.value === 'dashboard') {
      return false
    }

    if (!item.requiredPermissions?.length) {
      return true
    }

    return canAccessPermissions(
      userPermissions,
      item.requiredPermissions,
      'any'
    )
  })
    .map((item) => filterSidebarChildren(item, userPermissions))
    .filter((item) => {
      if (item.children?.length) {
        return true
      }

      return Boolean(item.link)
    })
}

export function getFirstAccessiblePath(
  userPermissions: readonly HrPermissionCode[],
  roles: readonly AdminRole[] = []
): string | null {
  const items = getAccessibleSidebarItems(userPermissions, roles)

  for (const item of items) {
    const childLink = item.children?.[0]?.link
    if (childLink) {
      return childLink
    }

    if (item.link) {
      return item.link
    }
  }

  return null
}

export function getAuthenticatedLandingPath(
  roles: readonly AdminRole[],
  userPermissions: readonly HrPermissionCode[]
): string | null {
  if (isApplicantRole(roles)) {
    if (
      canAccessPermissions(
        userPermissions,
        MODULE_PERMISSIONS.employmentForm,
        'any'
      )
    ) {
      return APPLICANT_LANDING_PATH
    }

    return getFirstAccessiblePath(userPermissions, roles)
  }

  return getFirstAccessiblePath(userPermissions, roles)
}
